
import React, { useState, useEffect } from 'react';
import { UserProfile, FocusSettings } from '../types';
import { Button } from '../components/Button';
import { STRIPE_LINKS, FREE_TIER_LIMIT, THEMES, LEVELS } from '../constants';
import { 
  Shield, Check, Clock, Save, Lock, X, Plus, 
  Crown, Zap, RefreshCw, Smartphone, Monitor, Layout, 
  CreditCard, Palette, Moon, Info, ExternalLink, Infinity, Sparkles, Map, CheckCircle2
} from 'lucide-react';
import { saveUserTheme, syncUserProfile, getPortalUrl } from '../services/firebaseService';

interface SettingsViewProps {
  user: UserProfile;
  taskCount: number;
  onUpdateSettings?: (settings: FocusSettings) => void;
  onUpgrade?: () => void;
  onPricingTrigger?: () => void;
}


export const SettingsView: React.FC<SettingsViewProps> = ({ 
  user, 
  taskCount, 
  onUpdateSettings,
  onUpgrade,
  onPricingTrigger,
}) => {


  const handleUpgrade = () => {
    // 👉 EXTENSÃO
    if (__APP_ENV__ === 'extension') {
      const url = 'https://app.eisenhowerpro.com/?upgrade=true&from=extension';

      if (typeof chrome !== 'undefined' && chrome.tabs) {
        chrome.tabs.create({ url });
      } else {
        window.open(url, '_blank');
      }
      return;
    }

    // 👉 WEB APP
    if (onUpgrade) {
      onUpgrade();
    }
  };




  // State
  const [settings, setSettings] = useState<FocusSettings>({
    workDuration: 25,
    shortBreakDuration: 5,
    longBreakDuration: 15,
    blockedSites: []
  });
  const [newSite, setNewSite] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(user.theme || 'light');

  // Load initial settings
  useEffect(() => {
    if (user.focusSettings) {
      setSettings(prev => ({
        ...prev,
        ...user.focusSettings,
        blockedSites: user.focusSettings?.blockedSites || []
      }));
    }
  }, [user.focusSettings]);

  // Gamification Calcs
  const currentLevelObj = LEVELS.find(l => l.level === user.level) || LEVELS[0];
  const nextLevelObj = LEVELS.find(l => l.level === user.level + 1);
  const xpCurrent = user.xp;
  const xpStart = currentLevelObj.xp;
  const xpEnd = nextLevelObj ? nextLevelObj.xp : xpCurrent * 1.5;
  const progressPercent = Math.min(100, Math.max(0, ((xpCurrent - xpStart) / (xpEnd - xpStart)) * 100));

  // Handlers
  const handleManageSubscription = async () => {
    setIsLoadingPortal(true);
    try {
      const url = await getPortalUrl();
      window.location.href = url;
    } catch (error) {
      alert("Could not open subscription management. Please check your connection.");
    } finally {
      setIsLoadingPortal(false);
    }
  };
  
  const handleRestorePurchase = async () => {
      setIsSyncing(true);
      await syncUserProfile(user.uid);
      setTimeout(() => setIsSyncing(false), 1000);
  };

  const handleSaveSettings = async () => {
    if (onUpdateSettings) {
      setIsSaving(true);
      try {
        await onUpdateSettings(settings);
        if (selectedTheme !== user.theme) {
            await saveUserTheme(user.uid, selectedTheme);
        }
      } finally {
        setTimeout(() => setIsSaving(false), 500);
      }
    }
  };

  const handleChange = (key: keyof FocusSettings, value: string) => {
    const num = parseInt(value);
    if (!isNaN(num) && num > 0) {
      setSettings(prev => ({ ...prev, [key]: num }));
    }
  };

  const handleAddSite = () => {
    if (!user.isPro) {
      handleUpgrade();
      return;
    }

    if (!newSite.trim()) return;
    let domain = newSite.trim().toLowerCase();
    domain = domain.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    
    if (domain && !settings.blockedSites?.includes(domain)) {
      setSettings(prev => ({
        ...prev,
        blockedSites: [...(prev.blockedSites || []), domain]
      }));
    }
    setNewSite('');
  };

  const handleRemoveSite = (site: string) => {
    setSettings(prev => ({
      ...prev,
      blockedSites: prev.blockedSites?.filter(s => s !== site)
    }));
  };

  const handleAddPreset = (type: 'social' | 'news') => {
      if (!user.isPro) {
        handleUpgrade();
        return;
      }
      const presets = type === 'social' 
        ? ['facebook.com', 'twitter.com', 'instagram.com', 'tiktok.com', 'linkedin.com']
        : ['cnn.com', 'bbc.com', 'nytimes.com', 'reddit.com'];
      
      const current = new Set(settings.blockedSites || []);
      presets.forEach(p => current.add(p));
      setSettings(prev => ({ ...prev, blockedSites: Array.from(current) }));
  };

  const usagePercent = Math.min(100, (taskCount / FREE_TIER_LIMIT) * 100);
  const usageColor = usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-yellow-500' : 'bg-blue-500';

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-24 animate-fade-in text-gray-900 dark:text-dark-text">
      
      {/* 1. GAMIFICATION JOURNEY (Compact Timeline) */}
      <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
           <div>
              <h2 className="text-lg font-bold flex items-center gap-2 text-gray-900 dark:text-white">
                 <Map className="text-amber-500" size={18} /> Your Journey
              </h2>
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                 <span className="font-semibold text-amber-600 dark:text-amber-400">{currentLevelObj.title} (Lvl {user.level})</span>
                 <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                 <span>{user.xp} / {xpEnd} XP</span>
              </p>
           </div>
           
           {/* Slim Progress Bar */}
           <div className="w-full sm:w-1/3">
              <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                 <span>Progress to Lvl {user.level + 1}</span>
                 <span>{Math.floor(progressPercent)}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                 <div 
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-1000"
                    style={{ width: `${progressPercent}%` }}
                 ></div>
              </div>
           </div>
        </div>

        {/* Timeline Scroll Container */}
        <div className="p-6 overflow-x-auto">
           <div className="min-w-[600px] flex items-start justify-between relative px-4">
              {/* Background Line */}
              <div className="absolute top-[15px] left-0 w-full h-0.5 bg-gray-100 dark:bg-gray-800 -z-0"></div>
              
              {/* Active Line (Approximate) */}
              <div 
                 className="absolute top-[15px] left-0 h-0.5 bg-amber-200 dark:bg-amber-900/30 -z-0 transition-all duration-1000"
                 style={{ width: `${(user.level / 6) * 100}%` }}
              ></div>

              {LEVELS.map((lvl) => {
                const isUnlocked = user.level >= lvl.level;
                const isCurrent = user.level === lvl.level;
                
                return (
                  <div key={lvl.level} className="flex flex-col items-center relative z-10 w-24 group">
                     {/* Node */}
                     <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                        isCurrent 
                           ? 'bg-amber-500 border-amber-500 text-white shadow-lg shadow-amber-500/30 scale-110' 
                           : isUnlocked
                             ? 'bg-white dark:bg-dark-surface border-amber-500 text-amber-500'
                             : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-300'
                     }`}>
                        {isUnlocked ? (
                           isCurrent ? <Sparkles size={14} /> : <Check size={14} strokeWidth={3} />
                        ) : (
                           <span className="text-xs font-bold">{lvl.level}</span>
                        )}
                     </div>

                     {/* Label */}
                     <div className="mt-3 text-center">
                        <div className={`text-xs font-bold mb-0.5 transition-colors ${
                           isCurrent ? 'text-gray-900 dark:text-white' : isUnlocked ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-600'
                        }`}>
                           {lvl.title}
                        </div>
                        <div className={`text-[10px] truncate max-w-[90px] mx-auto transition-colors ${
                           isUnlocked ? 'text-amber-600 dark:text-amber-500' : 'text-gray-400 dark:text-gray-600'
                        }`}>
                           {lvl.unlock.replace('Unlock: ', '')}
                        </div>
                     </div>
                  </div>
                )
              })}
           </div>
        </div>
      </div>

      {/* 2. SUBSCRIPTION & PLAN INFO */}
      <div className={`rounded-xl border shadow-sm p-6 overflow-hidden relative ${user.isPro ? 'bg-gradient-to-br from-gray-900 to-gray-800 text-white border-transparent' : 'bg-white dark:bg-dark-surface border-gray-200 dark:border-dark-border'}`}>
          <div className="flex flex-col md:flex-row gap-8">
              {/* Left: Status */}
              <div className="flex-1">
                  <div className="flex justify-between items-start mb-4">
                      <h3 className="font-bold text-lg flex items-center gap-2">
                          <CreditCard size={20} className={user.isPro ? 'text-yellow-400' : 'text-gray-400'} />
                          {user.isPro ? 'Pro Member' : 'Free Plan'}
                      </h3>
                      {user.isPro && <span className="bg-yellow-400/20 text-yellow-400 text-xs px-2 py-1 rounded font-bold uppercase">Active</span>}
                  </div>

                  {!user.isPro ? (
                      <div className="space-y-4">
                          <div>
                              <div className="flex justify-between text-xs mb-1 font-medium text-gray-500 dark:text-gray-400">
                                  <span>Task Limit</span>
                                  <span>{taskCount} / {FREE_TIER_LIMIT}</span>
                              </div>
                              <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div className={`h-full transition-all ${usageColor}`} style={{ width: `${usagePercent}%` }}></div>
                              </div>
                          </div>
                          
                          <Button
                            onClick={handleUpgrade}
                            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 border-none shadow-lg"
                          >
                            Upgrade to Pro
                          </Button>
                      </div>
                  ) : (
                      <div className="space-y-4">
                         <p className="text-gray-300 text-sm">
                             Thank you for your support. You have access to all features.
                         </p>
                         <div className="flex gap-2">
                             <Button 
                               onClick={handleManageSubscription} 
                               disabled={isLoadingPortal}
                               className="flex-1 bg-white/10 text-white border-white/20 hover:bg-white/20"
                             >
                                {isLoadingPortal ? <RefreshCw className="animate-spin" size={16}/> : 'Manage Subscription'}
                             </Button>
                             <Button variant="secondary" onClick={handleRestorePurchase} disabled={isSyncing} className="bg-white/10 text-white border-white/20 hover:bg-white/20">
                                {isSyncing ? <RefreshCw className="animate-spin" size={16}/> : 'Sync'}
                             </Button>
                         </div>
                      </div>
                  )}
              </div>

              {/* Right: Feature Matrix (Only visible if Free) */}
              {!user.isPro && (
                 <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-100 dark:border-gray-700 pt-4 md:pt-0 md:pl-8">
                    <h4 className="font-bold text-sm mb-3">Why Upgrade?</h4>
                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                       <li className="flex items-center gap-2"><Infinity size={16} className="text-blue-500"/> Unlimited Tasks</li>
                       <li className="flex items-center gap-2"><Shield size={16} className="text-blue-500"/> Deep Focus (Site Blocking)</li>
                       <li className="flex items-center gap-2"><Sparkles size={16} className="text-blue-500"/> Unlimited AI Sorting</li>
                       <li className="flex items-center gap-2"><Moon size={16} className="text-blue-500"/> Dark Mode</li>
                    </ul>
                 </div>
              )}
          </div>
      </div>

      {/* 3. CENTER: DEEP FOCUS CONTROL */}
      <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border shadow-sm overflow-hidden relative">
          <div className="p-6 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-dark-border flex justify-between items-center">
              <div>
                  <h3 className="font-bold text-lg flex items-center gap-2 text-gray-900 dark:text-white">
                      <Shield size={20} className="text-red-500" />
                      Deep Focus Blocking
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Sites in this list will be blocked while the timer is running.</p>
              </div>
              {!user.isPro && (
                  <span className="flex items-center gap-1 text-xs font-bold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                      <Lock size={12} /> PRO Feature
                  </span>
              )}
          </div>
          
          <div className="p-6 relative">
              {/* LOCK OVERLAY FOR FREE USERS */}
              {!user.isPro && (
                  <div className="absolute inset-0 z-10 bg-white/60 dark:bg-black/60 backdrop-blur-[1px] flex items-center justify-center">
                       <div className="text-center">
                           <Button onClick={handleUpgrade} className="shadow-xl">
                             Unlock Site Blocking
                           </Button>
                       </div>
                  </div>
              )}

              <div className="flex gap-2 mb-4">
                  <input 
                      type="text" 
                      value={newSite}
                      onChange={(e) => setNewSite(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddSite()}
                      placeholder="example.com"
                      disabled={!user.isPro}
                      className="flex-1 border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg px-4 focus:ring-2 focus:ring-red-500"
                  />
                  <Button onClick={handleAddSite} variant="secondary" disabled={!user.isPro}><Plus size={18} /></Button>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                  {settings.blockedSites?.map(site => (
                      <span key={site} className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-full text-sm font-medium border border-red-100 dark:border-red-900/30">
                          {site}
                          <button onClick={() => handleRemoveSite(site)} className="hover:text-red-900 dark:hover:text-red-200"><X size={14}/></button>
                      </span>
                  ))}
                  {(!settings.blockedSites || settings.blockedSites.length === 0) && (
                      <span className="text-sm text-gray-400 italic">No sites blocked yet.</span>
                  )}
              </div>

              <div className="flex gap-2">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wide py-1.5">Quick Add:</span>
                  <button onClick={() => handleAddPreset('social')} disabled={!user.isPro} className="text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-600 dark:text-gray-300 px-3 py-1.5 rounded-md transition-colors flex items-center gap-1">
                      <Smartphone size={12} /> Social Media
                  </button>
                  <button onClick={() => handleAddPreset('news')} disabled={!user.isPro} className="text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-600 dark:text-gray-300 px-3 py-1.5 rounded-md transition-colors flex items-center gap-1">
                      <Monitor size={12} /> News
                  </button>
              </div>
          </div>
      </div>

      {/* 4. TIMER & APPEARANCE GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border shadow-sm p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
                  <Clock size={20} className="text-gray-400" />
                  Timer Settings
              </h3>
              <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                      <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Focus</label>
                          <input 
                            type="number" 
                            value={settings.workDuration}
                            onChange={(e) => handleChange('workDuration', e.target.value)}
                            className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg text-center p-2 focus:ring-blue-500"
                          />
                      </div>
                      <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Short Break</label>
                          <input 
                            type="number" 
                            value={settings.shortBreakDuration}
                            onChange={(e) => handleChange('shortBreakDuration', e.target.value)}
                            className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg text-center p-2 focus:ring-emerald-500"
                          />
                      </div>
                      <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Long Break</label>
                          <input 
                            type="number" 
                            value={settings.longBreakDuration}
                            onChange={(e) => handleChange('longBreakDuration', e.target.value)}
                            className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg text-center p-2 focus:ring-emerald-500"
                          />
                      </div>
                  </div>
                  <p className="text-xs text-gray-400 text-center">Values in minutes</p>
              </div>
          </div>

          <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border shadow-sm p-6">
               <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
                  <Palette size={20} className="text-purple-500" />
                  Appearance
               </h3>
               <div className="flex flex-wrap gap-3">
                  {THEMES.map(theme => {
                     const isLocked = user.level < theme.minLevel;
                     const isActive = selectedTheme === theme.id;
                     return (
                       <button
                         key={theme.id}
                         disabled={isLocked}
                         onClick={() => setSelectedTheme(theme.id as any)}
                         className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                            isActive 
                              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 ring-1 ring-purple-500 font-medium' 
                              : isLocked 
                                ? 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-400 cursor-not-allowed opacity-70' 
                                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                         }`}
                       >
                          {theme.id === 'dark' || theme.id === 'midnight' ? <Moon size={16}/> : <Layout size={16}/>}
                          {theme.label}
                          {isLocked && <Lock size={12} className="ml-1 opacity-50"/>}
                       </button>
                     );
                   })}
               </div>
          </div>
      </div>

      {/* Floating Save Button */}
      <div className="fixed bottom-6 right-6 z-30">
        <Button 
            onClick={handleSaveSettings} 
            disabled={isSaving} 
            size="lg"
            className="shadow-xl shadow-blue-900/20"
        >
             {isSaving ? 'Saving...' : <><Save size={18} className="mr-2" /> Save Changes</>}
        </Button>
      </div>

    </div>
  );
};
